import { makeAutoObservable, runInAction } from 'mobx';

import useStore from '@/common/hooks/useStore';
import { DriveAPI } from '@/drive/api/DriveAPI';
import { TransientDriveItem } from '@/drive/entities/transientDriveItem';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';

class PinnedItemsDtoStore {
  private itemMap = new Map<string, TransientDriveItem>();

  private loadingIds = new Set<string>();

  get isLoading() {
    return this.loadingIds.size > 0;
  }

  constructor(private readonly driveAPI = new DriveAPI()) {
    makeAutoObservable<PinnedItemsDtoStore, 'baseFetch'>(this, {
      getLoadedItem: false,
      isLoadingItem: false,
      fetchIfMissing: false,
      baseFetch: false,
    });
  }

  getLoadedItem = (pinnedItemId: string): Nullable<TransientDriveItem> => {
    return this.itemMap.get(pinnedItemId) ?? null;
  };

  isLoadingItem = (pinnedItemId: string): boolean => {
    return this.loadingIds.has(pinnedItemId);
  };

  fetchIfMissing = async (items: PinnedItem[]): Promise<Nullable<TransientDriveItem>[]> => {
    await this.fetch(items.filter((item) => !this.itemMap.has(item.id)));

    return items.map((item) => this.itemMap.get(item.id) ?? null);
  };

  fetch = async (items: PinnedItem[]) => {
    const toLoad = items.filter((item) => !this.loadingIds.has(item.id));

    if (toLoad.length === 0) {
      return;
    }

    toLoad.forEach((item) => this.loadingIds.add(item.id));

    try {
      await this.baseFetch(toLoad);
    } finally {
      runInAction(() => {
        toLoad.forEach((item) => this.loadingIds.delete(item.id));
      });
    }
  };

  private baseFetch = async (items: PinnedItem[]) => {
    const res = await this.driveAPI.makeDto(items.map((item) => item.toDriveItemRequest()));

    runInAction(() => {
      res.getItemsByRequestIndex().forEach((loaded, index) => {
        if (loaded) {
          this.itemMap.set(items[index].id, loaded);
        }
      });
    });
  };
}

export const usePinnedItemsDtoStore = () => useStore(PinnedItemsDtoStore);

export const pinnedItemsDtoStore = new PinnedItemsDtoStore();

export default PinnedItemsDtoStore;
